/**
 * Store API Hooks
 * Custom hooks for interacting with the Store API
 */
import { useState, useEffect, useCallback } from 'react';

// API base URL - will be injected via environment variable during build
const API_BASE_URL = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';

/**
 * Helper function to append user_id query parameter to admin URLs
 */
const appendUserId = (url, userId) => {
  if (!userId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}user_id=${encodeURIComponent(userId)}`;
};

/**
 * Custom hook for fetching data with loading and error states
 */
const useFetch = (url, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    // Skip fetching when there's no URL (e.g. hook called for a guest).
    if (!url) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (!isCancelled) {
          setData(result);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [url]); // Only depend on url, not options

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [url, options]);

  return { data, loading, error, refetch };
};

/**
 * Fetch a curated list of featured stores for public showcase (login page, etc).
 */
export const useFeaturedStores = (limit = 8) => {
  const { data, loading, error, refetch } = useFetch(
    `${API_BASE_URL}/stores/featured?limit=${limit}`
  );

  return {
    stores: data?.stores || [],
    loading,
    error,
    refetch,
  };
};

/**
 * Fetch store metadata by slug
 */
export const useStore = (storeSlug, userId = null) => {
  // Include user_id so backend can return is_owner flag without exposing owner_id
  const qp = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
  return useFetch(`${API_BASE_URL}/store/${storeSlug}${qp}`);
};

/**
 * Fetch all products for a store (single fetch)
 * NOTE: Category-based filtering is now done client-side to avoid
 * repeated network requests when switching categories.
 * The second parameter (categoryId) is kept for backward compatibility
 * but is intentionally ignored.
 */
export const useStoreProducts = (storeSlug, _categoryId = null) => {
  // Always fetch the full product list once; local filtering handles categories
  const url = `${API_BASE_URL}/store/${storeSlug}/products`;

  const { data, loading, error, refetch } = useFetch(url);

  return {
    data: data?.products || [],
    loading,
    error,
    refetch
  };
};

/**
 * Fetch all categories for a store
 */
export const useStoreCategories = (storeSlug) => {
  const { data, loading, error, refetch } = useFetch(`${API_BASE_URL}/store/${storeSlug}/categories`);

  // Extract categories array from response
  return {
    data: data?.categories || [],
    loading,
    error,
    refetch
  };
};

/**
 * Fetch delivery points for a store
 */
export const useStoreDeliveryPoints = (storeSlug) => {
  const { data, loading, error, refetch } = useFetch(`${API_BASE_URL}/store/${storeSlug}/delivery-points`);

  // Extract delivery_points array from response
  return {
    data: data?.delivery_points || [],
    loading,
    error,
    refetch
  };
};

/**
 * Fetch delivery areas for a store (public listing for delivery selection)
 */
export const useStoreDeliveryAreas = (storeSlug) => {
  const { data, loading, error, refetch } = useFetch(`${API_BASE_URL}/store/${storeSlug}/delivery-points?mode=areas`);
  return {
    data: data?.delivery_areas || [],
    loading,
    error,
    refetch,
  };
};


/**
 * Unified delivery fetch (points + areas in one request to avoid double fetches)
 * Uses mode=all which backend returns { delivery_points: [...], delivery_areas: [...] }
 */
export const useStoreDelivery = (storeSlug) => {
  const { data, loading, error, refetch } = useFetch(`${API_BASE_URL}/store/${storeSlug}/delivery-points?mode=all`);
  return {
    deliveryPoints: data?.delivery_points || [],
    deliveryAreas: data?.delivery_areas || [],
    loading,
    error,
    refetch,
  };
};

export const useStoreDeliveryAdmin = (storeId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/delivery-points`, userId);
  const { data, loading, error, refetch } = useFetch(url);
  return {
    deliveryPoints: data?.delivery_points || [],
    deliveryAreas: data?.delivery_areas || [],
    loading,
    error,
    refetch,
  };
};

/**
 * Fetch FAQs for a store
 */
export const useStoreFAQ = (storeSlug) => {
  const { data, loading, error, refetch } = useFetch(`${API_BASE_URL}/store/${storeSlug}/faq`);

  // Extract faqs array from response
  return {
    data: data?.faqs || [],
    loading,
    error,
    refetch
  };
};

/**
 * Create a new store
 */
export const createStore = async (storeData) => {
  const response = await fetch(`${API_BASE_URL}/store`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(storeData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create store');
  }

  return response.json();
};

/**
 * Update store metadata (admin)
 */
export const updateStore = async (storeId, updates, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update store');
  }

  return response.json();
};

/**
 * Delete a store (admin)
 */
export const deleteStore = async (storeId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}`, userId);
  const response = await fetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete store');
  }

  return response.json();
};

/**
 * Create a product (admin)
 */
export const createProduct = async (storeId, productData, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/products`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(productData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create product');
  }

  return response.json();
};

/**
 * Update a product (admin)
 */
export const updateProduct = async (storeId, productId, updates, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/products/${productId}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update product');
  }

  return response.json();
};

/**
 * Delete a product (admin)
 */
export const deleteProduct = async (storeId, productId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/products/${productId}`, userId);
  const response = await fetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete product');
  }

  return response.json();
};

/**
 * Duplicate a product (admin)
 * Creates a fully independent copy of the product, including its own image files
 */
export const duplicateProduct = async (storeId, productId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/products/${productId}/duplicate`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to duplicate product');
  }

  return response.json();
};

/**
 * Reorder a product (admin)
 * Moves a product up or down within its category
 */
export const reorderProduct = async (storeId, productId, direction, categoryId = null, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/products/${productId}/reorder`, userId);
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      direction,
      category_id: categoryId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reorder product');
  }

  return response.json();
};

/**
 * Batch reorder products (admin)
 * Applies multiple reorder operations in sequence
 */
export const batchReorderProducts = async (storeId, reorders, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/products/batch-reorder`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      reorders: reorders.map(r => ({
        product_id: r.productId,
        direction: r.direction,
        category_id: r.categoryId
      }))
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to batch reorder products');
  }

  return response.json();
};

/**
 * Normalize product sort orders (admin)
 * Resets all product sort_order values to sequential numbers per category
 */
export const normalizeProductSortOrders = async (storeId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/products/normalize`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to normalize product sort orders');
  }

  return response.json();
};

/**
 * Create a category (admin)
 */
export const createCategory = async (storeId, categoryData, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/categories`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(categoryData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create category');
  }

  return response.json();
};

/**
 * Update a category (admin)
 */
export const updateCategory = async (storeId, categoryId, updates, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/categories/${categoryId}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update category');
  }

  return response.json();
};

/**
 * Delete a category (admin)
 */
export const deleteCategory = async (storeId, categoryId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/categories/${categoryId}`, userId);
  const response = await fetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete category');
  }

  return response.json();
};

/**
 * Reorder a category (admin)
 * Moves a category up or down in the display order
 */
export const reorderCategory = async (storeId, categoryId, direction, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/categories/${categoryId}/reorder`, userId);
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      direction,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reorder category');
  }

  return response.json();
};

/**
 * Create a delivery point (admin)
 */
export const createDeliveryPoint = async (storeId, dpData, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/delivery-points`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(dpData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create delivery point');
  }

  return response.json();
};

/**
 * Update a delivery point (admin)
 */
export const updateDeliveryPoint = async (storeId, dpId, updates, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/delivery-points/${dpId}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update delivery point');
  }

  return response.json();
};

/**
 * Delete a delivery point (admin)
 */
export const deleteDeliveryPoint = async (storeId, dpId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/delivery-points/${dpId}`, userId);
  const response = await fetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete delivery point');
  }

  return response.json();
};

/**
 * Create delivery area(s) (admin)
 * Accepts either single area object ({ name, fee?, min_order? }) or bulk via names array
 * For bulk provide: { area_names: ['City1','City2'], fee?, min_order? }
 */
export const createDeliveryArea = async (storeId, areaData, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/delivery-points`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...areaData, is_area: true }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create delivery area');
  }
  return response.json();
};

/**
 * Update a delivery area (admin)
 */
export const updateDeliveryArea = async (storeId, areaId, updates, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/delivery-points/${areaId}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...updates, is_area: true }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update delivery area');
  }
  return response.json();
};

/**
 * Delete a delivery area (admin)
 */
export const deleteDeliveryArea = async (storeId, areaId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/delivery-points/${areaId}`, userId);
  const response = await fetch(url, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to delete delivery area');
  }
  return response.json();
};

/**
 * Create a coupon (admin)
 */
export const createCoupon = async (storeId, couponData, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/coupons`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(couponData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create coupon');
  }

  return response.json();
};

/**
 * Update a coupon (admin)
 */
export const updateCoupon = async (storeId, couponCode, updates, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/coupons/${couponCode}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update coupon');
  }

  return response.json();
};

/**
 * Delete a coupon (admin)
 */
export const deleteCoupon = async (storeId, couponCode, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/coupons/${couponCode}`, userId);
  const response = await fetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete coupon');
  }

  return response.json();
};

/**
 * Get all coupons (admin)
 */
export const getCoupons = async (storeId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/coupons`, userId);
  const response = await fetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch coupons');
  }

  const data = await response.json();
  return data.coupons || [];
};

/**
 * Create an FAQ (admin)
 */
export const createFAQ = async (storeId, faqData, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/faq`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(faqData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create FAQ');
  }

  return response.json();
};

/**
 * Update an FAQ (admin)
 */
export const updateFAQ = async (storeId, faqId, updates, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/faq/${faqId}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update FAQ');
  }

  return response.json();
};

/**
 * Delete an FAQ (admin)
 */
export const deleteFAQ = async (storeId, faqId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/faq/${faqId}`, userId);
  const response = await fetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete FAQ');
  }

  return response.json();
};

/**
 * Fetch orders for a store (admin)
 */
export const useStoreOrders = (storeId, status = null, userId = null) => {
  let url = status
    ? `${API_BASE_URL}/admin/store/${storeId}/orders?status=${status}`
    : `${API_BASE_URL}/admin/store/${storeId}/orders`;

  url = appendUserId(url, userId);

  return useFetch(url);
};

/**
 * Update order status (admin)
 */
export const updateOrderStatus = async (storeId, orderId, status, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/orders/${orderId}`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update order status');
  }

  return response.json();
};

/**
 * Archive many orders in a single atomic request.
 * Returns { archived_ids, failed_ids, archived_count, requested_count }.
 * A failed archive never changes the order (it is reported in failed_ids),
 * so archiving can never fall through to cancelling a real order.
 */
export const bulkArchiveOrders = async (storeId, orderIds, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/orders/bulk-archive`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ order_ids: orderIds }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to archive orders');
  }

  return response.json();
};

/**
 * Place an order (customer)
 */
export const placeOrder = async (storeSlug, orderData) => {
  const response = await fetch(`${API_BASE_URL}/store/${storeSlug}/orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to place order');
  }

  return response.json();
};

/**
 * Register a logged-in customer to the store's customers club.
 * Requires a real userId (the club is not available to guests).
 */
export const registerClubMember = async (storeSlug, { userId, name, email = null, phone = null }) => {
  const response = await fetch(`${API_BASE_URL}/store/${storeSlug}/club/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id: userId, name, email: email || undefined, phone: phone || undefined }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to register to customers club');
  }

  return response.json();
};

/**
 * Fetch the customers-club member list (admin)
 */
export const useStoreClubMembers = (storeId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/club/members`, userId);
  return useFetch(url);
};

/**
 * Fetch a user's customers-club referral coupons across all stores (homepage)
 */
export const useUserClubs = (userId) => {
  const isReal = userId && userId !== 'guest';
  const url = isReal ? `${API_BASE_URL}/club/my?user_id=${encodeURIComponent(userId)}` : null;
  return useFetch(url);
};

/**
 * Validate a coupon (customer)
 */
export const validateCoupon = async (storeSlug, couponCode, orderTotal, userId = null) => {
  const response = await fetch(`${API_BASE_URL}/store/${storeSlug}/coupons/validate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      coupon_code: couponCode,
      order_total: orderTotal,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate coupon');
  }

  return response.json();
};

/**
 * Get presigned URL for image upload (admin)
 */
export const getUploadUrl = async (storeId, fileType, fileName, uploadType, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/upload-url`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      file_type: fileType,
      file_name: fileName,
      upload_type: uploadType,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get upload URL');
  }

  return response.json();
};

/**
 * Upload image to S3 using presigned URL
 */
export const uploadImageToS3 = async (presignedUrl, file, contentType) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error('Failed to upload image');
  }

  return true;
};

/**
 * Check if a store slug is available
 */
export const checkSlugAvailability = async (slug) => {
  const response = await fetch(`${API_BASE_URL}/store/check-availability?slug=${encodeURIComponent(slug)}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check slug availability');
  }

  return response.json();
};

/**
 * Fetch enabled payment methods for a store (customer side)
 * Uses store_slug as the endpoint expects slug, not ID
 */
export const useStorePaymentMethods = (storeSlug) => {
  const { data, loading, error, refetch } = useFetch(
    storeSlug ? `${API_BASE_URL}/store/${storeSlug}/payment-methods` : null
  );

  return {
    data: data || { payment_methods: ["Bit", "PayBox", "מזומן", "העברה בנקאית"] },
    loading,
    error,
    refetch
  };
};

/**
 * Get payment methods (admin)
 */
export const getStorePaymentMethodsAdmin = async (storeId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/payment-methods`, userId);
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch payment methods');
  }
  return response.json();
};

/**
 * Update enabled payment methods (admin)
 * methods: array of strings
 */
export const updateStorePaymentMethods = async (storeId, methods, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/payment-methods`, userId);
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payment_methods: methods }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update payment methods');
  }
  return response.json();
};

/**
 * Update Tranzila credit-card configuration (admin).
 * Pass empty strings for password / response_secret to clear them; omit to leave unchanged.
 */
export const updateStoreTranzilaConfig = async (storeId, { enabled, terminalName, terminalPassword, responseSecret }, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}`, userId);
  const body = {};
  if (enabled !== undefined) body.tranzila_enabled = !!enabled;
  if (terminalName !== undefined) body.tranzila_terminal_name = terminalName;
  if (terminalPassword !== undefined) body.tranzila_terminal_password = terminalPassword;
  if (responseSecret !== undefined) body.tranzila_response_secret = responseSecret;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update Tranzila configuration');
  }
  return response.json();
};

/**
 * Request a Tranzila handshake token (`thtk`) for a checkout amount.
 * Public endpoint — no auth required.
 */
export const createTranzilaHandshake = async (storeSlug, amount) => {
  const response = await fetch(`${API_BASE_URL}/store/${storeSlug}/payment/handshake`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to initialise card payment');
  }
  return response.json();
};

/**
 * Create store co-admin invitation (admin)
 * Generates a time-limited invitation link for another user to become a co-admin
 */
export const createStoreInvite = async (storeId, inviteeIdentifier = '', userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/invite`, userId);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ invitee_identifier: inviteeIdentifier }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create invitation');
  }

  return response.json();
};

/**
 * Accept store co-admin invitation
 * Validates token and adds user as co-admin
 */
export const acceptStoreInvite = async (token, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/store-invite/accept?token=${encodeURIComponent(token)}`, userId);
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to accept invitation');
  }

  return response.json();
};

/**
 * Remove co-admin from store (admin)
 * Only the store owner can remove co-admins
 */
export const removeCoAdmin = async (storeId, userIdToRemove, requestingUserId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/co-admin/${userIdToRemove}`, requestingUserId);
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to remove co-admin');
  }

  return response.json();
};

/**
 * Time Slots Management Hooks
 */

/**
 * Fetch time slots configuration for a store (admin)
 */
export const useStoreTimeSlots = (storeId, userId = null) => {
  const url = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/time-slots`, userId);
  const { data, loading, error, refetch } = useFetch(url);

  const timeSlots = data?.time_slots || [];
  const timeSlotsEnabled = data?.time_slots_enabled || false;

  // Update full configuration
  const updateConfig = useCallback(async (config) => {
    const updateUrl = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/time-slots`, userId);
    const response = await fetch(updateUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update time slots configuration');
    }

    return response.json();
  }, [storeId, userId]);

  // Add a new slot
  const addSlot = useCallback(async (slotData) => {
    const addUrl = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/time-slots`, userId);
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(slotData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to add time slot');
    }

    return response.json();
  }, [storeId, userId]);

  // Update a specific slot
  const updateSlot = useCallback(async (slotId, slotData) => {
    const updateUrl = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/time-slots/${slotId}`, userId);
    const response = await fetch(updateUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(slotData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to update time slot');
    }

    return response.json();
  }, [storeId, userId]);

  // Delete a slot
  const deleteSlot = useCallback(async (slotId) => {
    const deleteUrl = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/time-slots/${slotId}`, userId);
    const response = await fetch(deleteUrl, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to delete time slot');
    }

    return response.json();
  }, [storeId, userId]);

  // Clear all time slot usage (reset all bookings)
  const clearAllUsage = useCallback(async () => {
    const clearUrl = appendUserId(`${API_BASE_URL}/admin/store/${storeId}/time-slots/clear-usage`, userId);
    const response = await fetch(clearUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to clear time slot usage');
    }

    return response.json();
  }, [storeId, userId]);

  return {
    timeSlots,
    timeSlotsEnabled,
    loading,
    error,
    refetch,
    updateConfig,
    addSlot,
    updateSlot,
    deleteSlot,
    clearAllUsage
  };
};

/**
 * Fetch available dates with time slots (public)
 */
export const fetchAvailableDates = async (storeSlug, days = 7) => {
  const url = `${API_BASE_URL}/store/${storeSlug}/time-slots/available-dates?days=${days}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch available dates');
  }

  return response.json();
};

/**
 * Fetch time slot availability for a specific date (public)
 */
export const fetchTimeSlotAvailability = async (storeSlug, date) => {
  const url = `${API_BASE_URL}/store/${storeSlug}/time-slots/availability?date=${date}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch time slot availability');
  }

  return response.json();
};

